"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { ArrowRight, ChevronDown, ExternalLink, Layers } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CaseStudiesGridConsumer({
  isVisible
}: {
  isVisible: boolean;
}) {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  if (loading && posts.length === 0) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[...Array(4)].map((_, i) => <Skeleton key={i} data-index={i} className="aspect-[16/10] w-full rounded-lg" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-20 bg-accent/30 rounded-lg border border-border">
        <p className="text-destructive font-medium mb-4">Failed to load case studies.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>Retry</Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-32 bg-accent/20 rounded-lg border border-dashed border-border">
        <div className="inline-flex p-4 rounded-lg bg-accent mb-6">
          <Layers className="size-8 text-muted-foreground" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-2 font-sans">No projects found</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Our portfolio is currently being updated with our latest success stories. Check back soon.
        </p>
      </div>;
  }
  return <div className="space-y-20">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
        {posts.map((post, i) => <Link to={post.link || "#"} key={post.id} data-index={i} style={{
        transitionDelay: `${i * 150}ms`
      }} className={`group flex flex-col bg-card rounded-lg border border-border shadow-sm hover:shadow-xl transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] overflow-hidden ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative aspect-[16/10] overflow-hidden bg-muted">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" decoding="async" /> : <div className="absolute inset-0 flex items-center justify-center bg-accent">
                  <Layers className="size-12 text-muted-foreground/20" />
                </div>}
              
              <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>

            <div className="flex flex-col flex-grow p-8 md:p-10">
              <div className="flex justify-between items-start gap-6 mb-6">
                <div className="space-y-2">
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground leading-tight font-sans group-hover:text-primary transition-colors duration-300">
                    {post.title}
                  </h3>
                </div>
                <div className="shrink-0 p-3 rounded-lg bg-accent group-hover:bg-primary group-hover:text-white transition-all duration-500">
                  <ArrowRight className="size-6" />
                </div>
              </div>

              {post.excerpt && <div className="text-base text-muted-foreground line-clamp-3 mb-8 leading-relaxed" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />}
              
              <div className="mt-auto pt-6 border-t border-border flex items-center justify-between text-xs font-mono font-bold uppercase tracking-widest text-muted-foreground group-hover:text-foreground transition-colors duration-300">
                <span className="flex items-center gap-2">
                  Eksploruj Case Study
                  <ExternalLink className="size-3" />
                </span>
              </div>
            </div>
          </Link>)}
      </div>

      {hasNext && <div className="flex justify-center pt-8">
          <Button variant="outline" size="lg" onClick={async e => {
        e.preventDefault();
        await loadMore();
      }} disabled={isRefetching} className="rounded-lg px-12 py-8 text-sm font-bold uppercase tracking-widest border-2 hover:bg-primary hover:border-primary hover:text-white transition-all duration-300 shadow-md group">
            {isRefetching ? "Loading..." : <span className="flex items-center gap-3">
                Więcej Projektów
                <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
              </span>}
          </Button>
        </div>}
    </div>;
}
export default function CaseStudiesShowcaseGrid() {
  const {
    ref: revealRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const wp_query_params = {
    post_type: "post",
    per_page: 4,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="case-studies-showcase-grid" className="relative py-10 md:py-14 bg-background overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20">
        <div className="absolute top-[-10%] left-[-10%] w-[120%] h-[120%] bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_70%)] animate-[pulse_8s_infinite_ease-in-out]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[80%] h-[80%] bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_60%)] animate-[pulse_12s_infinite_ease-in-out_reverse]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6 overflow-visible">
        <div ref={revealRef} className={`w-full mb-12 md:mb-16 pb-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-bold font-sans tracking-tight mb-6 w-full !text-center transition-all duration-1000 bg-linear-to-r from-primary via-red-400 to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite] bg-clip-text text-transparent !leading-[1.1] !text-[40px] !h-auto md:!text-[120px] md:!h-auto md:leading-none">Dobry marketing nie krzyczy. Przekonuje.</h2>
          <div className="relative group max-w-3xl mx-auto">
            <div className="absolute -inset-4 bg-primary/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
            <p className="relative text-lg text-muted-foreground leading-relaxed !text-center transition-colors duration-300 group-hover:text-foreground !font-normal">Strategia, design i realizacja dla marek budujących trwałą pozycję rynkową.</p>
            <div className="relative flex flex-wrap items-center justify-center gap-4 mt-8">
              <Link to="/oferta">
                <Button size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-primary text-primary-foreground h-10 has-[>svg]:px-4 px-8 py-6 text-sm shadow-md transition-transform !rounded-3xl disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 hover:scale-105">Zobacz ofertę</Button>
              </Link>
              <Link to="/ContactPage">
                <Button size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-transparent text-foreground h-10 has-[>svg]:px-4 px-8 py-6 text-sm shadow-sm transition-all !rounded-4xl disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-accent/10">lub umów spotkanie<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right size-4" aria-hidden="true" data-ast-id="f:/src/pages/Homepage/sections/CaseStudiesShowcaseGrid.tsx|sig:dlwe5k" data-ast-start="8543" data-ast-end="8851"><path d="M5 12h14" data-ast-id="f:/src/pages/Homepage/sections/CaseStudiesShowcaseGrid.tsx|sig:bri4xm" data-ast-start="8788" data-ast-end="8814"></path><path d="m12 5 7 7-7 7" data-ast-id="f:/src/pages/Homepage/sections/CaseStudiesShowcaseGrid.tsx|sig:brxdnj" data-ast-start="8814" data-ast-end="8845"></path></svg></Button>
              </Link>
            </div>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 4,
        order: "desc",
        orderby: "date"
      }}>
          <CaseStudiesGridConsumer isVisible={isVisible} />
          
          <div className={`mt-12 text-center transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="inline-block p-6 sm:p-8 md:p-12 rounded-lg bg-card border border-border shadow-lg max-w-4xl w-full">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4 font-sans">
                Jeśli zależy Ci na tym, żeby
              </h3>
              <p className="text-base md:text-lg text-muted-foreground mb-8 md:mb-10 max-w-2xl mx-auto">
               Twoja marka była postrzegana tak, jak na to zasługuje - porozmawiajmy.
              </p>
              <Link to="/ContactPage" className="block sm:inline-block w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto !whitespace-normal text-center rounded-lg px-6 md:px-10 py-6 md:py-7 text-sm md:text-base font-bold uppercase tracking-wide md:tracking-wider shadow-md hover:scale-105 transition-transform">
                  Umów spotkanie
                </Button>
              </Link>
            </div>
          </div>
        </PostsProvider>
      </div>

      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `
    }} />
    </section>;
}